import type {Ref} from 'react';

/**
 * Gives `value` to a ref received as a prop (object or callback), for a component that also keeps the
 * element (or instance) in a ref of its own. Use in a callback ref, returning its result: React 19 calls
 * the cleanup on unmount.
 *
 * @param {Ref} ref - Ref from the parent (may be missing)
 * @param {*} value - Element or instance
 * @returns {Function | undefined} - Cleanup that resets the ref
 */
export const setRef = <T, >(ref: Ref<T> | undefined, value: T): (() => void) | undefined => {
	if (typeof ref === 'function') {
		const cleanup = ref(value);

		return typeof cleanup === 'function' ? cleanup : () => ref(null);
	}

	if (ref) {
		ref.current = value;

		return () => {
			ref.current = null;
		};
	}

	return undefined;
};
